import { DotsThreeVertical } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/utils";
import { useState, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  AdminCustomFieldListOptionsDocument,
  AdminCustomFieldsDocument,
  CustomFieldListOption,
  useAdminCustomFieldListOptionsQuery,
  useUpsertCustomFieldListOptionMutation,
} from "@common/utils/graphql";
import { CreateOrEditListOptionDialog } from "./CreateOrEditListOptionDialog";
import { Body, Heading } from "@hypercard/web-components";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";

type Props = {
  customFieldEntityId: number;
  customFieldEntityName: string;
};

export const CustomListOptions = ({
  customFieldEntityId,
  customFieldEntityName,
}: Props) => {
  const { toast } = useToast();
  const [searchText, setSearchText] = useState("");
  const [createOrEditFormOpen, setCreateOrEditFormOpen] = useState(false);
  const [editOption, setEditOption] = useState<CustomFieldListOption | null>(
    null,
  );
  const [upsertCustomFieldListOption] = useUpsertCustomFieldListOptionMutation({
    refetchQueries: [
      AdminCustomFieldsDocument,
      AdminCustomFieldListOptionsDocument,
    ],
  });
  const { data, loading } = useAdminCustomFieldListOptionsQuery({
    variables: {
      customFieldEntityId,
      searchText: searchText === "" ? undefined : searchText,
    },
  });

  const listOptions = useMemo(() => {
    return data?.adminCustomFieldListOptions.__typename ===
      "QueryAdminCustomFieldListOptionsSuccess"
      ? data.adminCustomFieldListOptions.data.listOptions
      : [];
  }, [data]);

  const onOpenCreateOrEditForm =
    (option: CustomFieldListOption | null) => () => {
      setEditOption(option);
      setCreateOrEditFormOpen(true);
    };

  const onCloseCreatOrEditForm = () => {
    // setEditOption is not set to null bc it causes the modal to show the create form
    // during the close animation.
    setCreateOrEditFormOpen(false);
  };

  const handleDeleteOption = (option: CustomFieldListOption) => async () => {
    const action = option.isDeleted ? "enable" : "delete";
    try {
      const { data } = await upsertCustomFieldListOption({
        variables: {
          input: {
            uuid: option.uuid,
            name: option.name,
            value: option.value,
            customFieldEntityId,
            isDeleted: !option.isDeleted,
          },
        },
      });
      if (
        data?.upsertCustomFieldListOption.__typename !=
        "MutationUpsertCustomFieldListOptionSuccess"
      ) {
        throw new Error();
      }
      toast({
        title: `${customFieldEntityName} option has been ${action}d successfully`,
      });
    } catch (e) {
      toast({
        title: `Failed to ${action} ${customFieldEntityName} option`,
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading && listOptions.length === 0 && searchText === "") {
    return <FullPageLoader />;
  }

  if (listOptions.length === 0 && searchText === "") {
    return (
      <>
        <div className="flex grow flex-col items-center justify-center space-y-2">
          <Heading
            size="md"
            weight="medium"
            className="text-white"
          >{`No ${customFieldEntityName} Options`}</Heading>
          <Body
            size="md"
            weight="regular"
            className="text-center text-white/70"
          >{`Start by adding your first ${customFieldEntityName} option. This field will not appear to employees until options are added.`}</Body>
        </div>
        <CreateOrEditListOptionDialog
          open={createOrEditFormOpen}
          customFieldOption={editOption}
          customFieldEntityId={customFieldEntityId}
          customFieldEntityName={customFieldEntityName}
          onOpen={onOpenCreateOrEditForm(null)}
          onClose={onCloseCreatOrEditForm}
        />
      </>
    );
  }

  return (
    <>
      <Input
        className="shrink-0"
        type="search"
        placeholder="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <ul className="mt-4 space-y-4 overflow-y-auto scrollbar-thin">
        {listOptions.length === 0 && !loading ? (
          <li className="flex items-center justify-center gap-x-2">
            <Body size="md" weight="medium" className="text-white">
              No matching {customFieldEntityName} options found
            </Body>
          </li>
        ) : (
          listOptions.map((option) => (
            <li
              key={`custom-field-options--option-${option.id}`}
              className={cn("flex items-center justify-between", {
                "opacity-30": option.isDeleted,
              })}
            >
              <div className="space-y-1">
                <Body size="md" weight="medium">
                  {option.name}
                </Body>
                <Body size="sm" weight="regular" className="text-white/70">
                  {option.value}
                </Body>
              </div>
              <div className="flex items-center gap-x-4">
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <DotsThreeVertical className="size-8" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="bottom" align="end">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onClick={onOpenCreateOrEditForm(option)}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className={cn("text-error", {
                          "text-success": option.isDeleted,
                        })}
                        onClick={handleDeleteOption(option)}
                      >
                        {option.isDeleted ? "Enable" : "Delete"}
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          ))
        )}
      </ul>

      <div className="mt-4 flex grow flex-col justify-end">
        <CreateOrEditListOptionDialog
          open={createOrEditFormOpen}
          customFieldOption={editOption}
          customFieldEntityId={customFieldEntityId}
          customFieldEntityName={customFieldEntityName}
          onOpen={onOpenCreateOrEditForm(null)}
          onClose={onCloseCreatOrEditForm}
        />
      </div>
    </>
  );
};
